import { Flex } from '@/ui/Flex/Flex';
import HorizontalDivider from '@/ui/HorizontalDivider';
import { T } from '@/ui/Text/Text';
import { useIntl } from 'react-intl';
import { IconHometimer, IconIdentifier, IconLEDOffSecondary } from '@/icons';
import { Input } from '@/ui/Input/Input';
import { MultiSelect } from '@/ui/Select/Select';
import { ActionTurnOffLEDNodeType } from '../../Nodes/types';
import FAQSection from '../FAQSection';
import { StyledSettings, StyledSettingsContent } from '../styles';
import { GeneralSettingsType } from '../types';
import { generateLEDOptions } from '../../util/generateSettingsOptions';

function TurnOffLEDSettings({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  settings,
  selectedNode,
  updateNodeData,
  disabled,
}: {
  settings: GeneralSettingsType;
  selectedNode: ActionTurnOffLEDNodeType;
  updateNodeData: (id: string, newData: any) => void;
  disabled?: boolean;
}) {
  const intl = useIntl();

  const faqs = [
    {
      question: intl.formatMessage({
        defaultMessage: 'How does the turn off LED work?',
        id: 'MYm87S',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          '"Turn Off LED" allows you to turn off an LED with a specified channel and identifier. You can also set a fade out duration for a smoother transition when turning off the LED.',
        id: 'RT6UNi',
      }),
    },
    {
      question: intl.formatMessage({
        defaultMessage: 'How does the Fade Out Duration work?',
        id: 'jgSPzE',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'Fade Out Duration defines how long it takes for the LED to turn off after the action is triggered. You can set this duration in seconds, and the LED will gradually fade out over that time.',
        id: 'ReWOjl',
      }),
    },
  ];

  const relevantData = selectedNode.data.options;

  const ledOptions = generateLEDOptions(settings.leds);

  return (
    <StyledSettings data-testid="action-turnOffLED-settings">
      <Flex align="center" gap={8}>
        <IconLEDOffSecondary />
        <Flex direction="column">
          <T h3>
            {intl.formatMessage({
              defaultMessage: 'Turn Off LED',
              id: 'uHJm2I',
            })}
          </T>
        </Flex>
      </Flex>
      <StyledSettingsContent>
        <Flex direction="column" gap={8}>
          <MultiSelect
            disabled={disabled ?? false}
            required
            title={intl.formatMessage({
              defaultMessage: 'LED(s)',
              id: 'c8hZEV',
            })}
            icon={<IconIdentifier />}
            placeholder={intl.formatMessage({
              defaultMessage: 'Select LED(s)',
              id: 'kORkwT',
            })}
            size="small"
            options={ledOptions}
            onChange={(e) => {
              updateNodeData(selectedNode.id, {
                ...selectedNode.data,
                options: {
                  ...relevantData,
                  leds: e ? e.map((item) => item.value) : null,
                },
              });
            }}
            value={
              Array.isArray(relevantData.leds) && relevantData.leds.length > 0
                ? relevantData.leds.map((led) => ({
                    label: led.name,
                    value: {
                      channel: led.channel,
                      identifier: led.identifier,
                      name: led.name,
                    },
                  }))
                : null
            }
          />
        </Flex>
        <HorizontalDivider />
        <Flex justify="space-between" align="center">
          <T body1>
            {intl.formatMessage({
              defaultMessage: 'Fade Out Duration (in seconds)',
              id: 'EsUwrE',
            })}
          </T>
          <Input
            disabled={disabled ?? false}
            type="number"
            size="small"
            value={
              relevantData.rampDownDuration
                ? relevantData.rampDownDuration / 10
                : 0
            }
            onBlur={(e) => {
              updateNodeData(selectedNode.id, {
                ...selectedNode.data,
                options: {
                  ...relevantData,
                  rampDownDuration: Number(e) * 10,
                },
              });
            }}
            onChange={() => {}}
            stepSize={0.1}
            min={0}
            max={99}
            icon={<IconHometimer />}
          />
        </Flex>
        <HorizontalDivider />
      </StyledSettingsContent>

      <FAQSection faqs={faqs} />
    </StyledSettings>
  );
}
export default TurnOffLEDSettings;
